// Implements: phase 4 toast polish per .claude/skills/rehaut-design/HANDOFF.md
'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ── Types ────────────────────────────────────────────────────

export type ToastKind = 'success' | 'error' | 'info';

export type ToastAction = {
  label: string;
  onClick: () => void | Promise<void>;
};

export type ToastInput = {
  kind?: ToastKind;
  message: string;
  /** Auto-dismiss duration in ms. Defaults: success/info 4000, error 6000. */
  duration?: number;
  /** Optional inline action (e.g. "Deshacer"). Clicking dismisses the toast. */
  action?: ToastAction;
  /** Called when the toast is dismissed without the action being triggered. */
  onAutoDismiss?: () => void;
};

type ToastItem = ToastInput & {
  id: string;
  kind: ToastKind;
  duration: number;
};

type ToastContextValue = {
  toast: (input: ToastInput) => string;
  dismiss: (id: string) => void;
};

// ── Context ──────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Hook for triggering toasts. Safe to call even if no provider is mounted —
 * returns a no-op so consumer code never crashes.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  return {
    toast: (input) => {
      // eslint-disable-next-line no-console
      console.warn('[useToast] No ToastProvider mounted. Message:', input.message);
      return '';
    },
    dismiss: () => {},
  };
}

// ── Provider ─────────────────────────────────────────────────

let idCounter = 0;
const nextId = () => `toast-${Date.now()}-${++idCounter}`;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Toasts whose action was clicked — suppresses onAutoDismiss.
  const actioned = useRef<Set<string>>(new Set());
  // Toasts whose terminal callback already ran — prevents StrictMode double-invoke
  // from committing the same removal twice.
  const finalized = useRef<Set<string>>(new Set());
  // Mirror of items so we can look up a toast outside a setState updater,
  // ensuring side effects run exactly once instead of once per updater replay.
  const itemsRef = useRef<ToastItem[]>([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const finalize = useCallback((id: string, viaAction: boolean) => {
    if (finalized.current.has(id)) return;
    finalized.current.add(id);
    const target = itemsRef.current.find((t) => t.id === id);
    if (target && !viaAction && target.onAutoDismiss) {
      // Defer so the commit runs after React flushes the state update below.
      Promise.resolve().then(() => {
        try { target.onAutoDismiss?.(); } catch { /* consumer owns error reporting */ }
      });
    }
    setItems((prev) => prev.filter((t) => t.id !== id));
    const handle = timeouts.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timeouts.current.delete(id);
    }
    actioned.current.delete(id);
  }, []);

  const dismiss = useCallback((id: string) => {
    finalize(id, false);
  }, [finalize]);

  const triggerAction = useCallback((id: string, action: ToastAction) => {
    actioned.current.add(id);
    finalize(id, true);
    void action.onClick();
  }, [finalize]);

  const toast = useCallback(
    (input: ToastInput): string => {
      const id = nextId();
      const kind: ToastKind = input.kind ?? 'info';
      const defaultDuration = kind === 'error' ? 6000 : input.action ? 5000 : 4000;
      const duration = input.duration ?? defaultDuration;

      setItems((prev) => [...prev, { ...input, id, kind, duration }]);

      const handle = setTimeout(() => finalize(id, false), duration);
      timeouts.current.set(id, handle);

      return id;
    },
    [finalize]
  );

  // Cleanup on unmount: invoke onAutoDismiss for any still-visible toasts so
  // pending undo-able operations get committed (e.g. user navigates away).
  useEffect(() => {
    const map = timeouts.current;
    return () => {
      map.forEach((h) => clearTimeout(h));
      map.clear();
      for (const t of itemsRef.current) {
        if (finalized.current.has(t.id)) continue;
        finalized.current.add(t.id);
        if (!actioned.current.has(t.id)) {
          try { t.onAutoDismiss?.(); } catch { /* consumer owns error reporting */ }
        }
      }
      actioned.current.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport
        items={items}
        onDismiss={dismiss}
        onAction={triggerAction}
      />
    </ToastContext.Provider>
  );
}

// ── Viewport (the visual stack) ──────────────────────────────

function ToastViewport({
  items,
  onDismiss,
  onAction,
}: {
  items: ToastItem[];
  onDismiss: (id: string) => void;
  onAction: (id: string, action: ToastAction) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        pointerEvents: 'none',
        maxWidth: 'min(420px, calc(100vw - 32px))',
      }}
    >
      {items.map((t) => (
        <ToastView
          key={t.id}
          item={t}
          onDismiss={() => onDismiss(t.id)}
          onAction={t.action ? () => onAction(t.id, t.action!) : undefined}
        />
      ))}
    </div>
  );
}

function ToastView({
  item,
  onDismiss,
  onAction,
}: {
  item: ToastItem;
  onDismiss: () => void;
  onAction?: () => void;
}) {
  const accent =
    item.kind === 'success' ? 'var(--ok-ink)' : item.kind === 'error' ? 'var(--error)' : 'var(--accent)';

  const isError = item.kind === 'error';

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onDismiss();
      }}
      tabIndex={-1}
      style={{
        pointerEvents: 'auto',
        background: 'var(--surface)',
        color: 'var(--ink-2)',
        border: `1px solid var(--hairline)`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 'var(--r-md)',
        padding: '12px 14px 12px 16px',
        boxShadow: '0 8px 24px rgba(20,20,19,0.10), 0 1px 2px rgba(20,20,19,0.04)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        lineHeight: 1.4,
        animation: 'jb-toast-in 150ms var(--ease-quiet)',
      }}
    >
      <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{item.message}</span>
      {item.action && onAction && (
        <button
          type="button"
          onClick={onAction}
          style={{
            background: 'transparent',
            border: 'none',
            color: accent,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
            fontFamily: 'var(--font-sans)',
            padding: '4px 8px',
            borderRadius: 'var(--r-sm)',
            flexShrink: 0,
          }}
        >
          {item.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar notificación"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--ink-4)',
          cursor: 'pointer',
          padding: '4px',
          display: 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>

      <style>{`
        @keyframes jb-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
